import { MapboxSource, useMapStore } from '@/features/hazards';
import { Point as turfPoint } from '@turf/turf';
import { LngLatLike, Marker } from 'mapbox-gl';
import { h, render } from 'vue';
import UiMapIncidentAreaMarker from '../components/UiMap/UiMapIncidentAreaMarker/UiMapIncidentAreaMarker.vue';

const INCIDENT_AREA_MARKER_VERTICAL_OFFSET = 24; // Offset in pixels of the incident area marker from the incident marker.
const INCIDENT_AREA_MARKER_HEIGHT = 28; // Height of the incident area marker in pixels
const INCIDENT_AREA_MARKER_TOTAL_VERTICAL_OFFSET = INCIDENT_AREA_MARKER_VERTICAL_OFFSET + INCIDENT_AREA_MARKER_HEIGHT; // Total offset of the incident area marker from the incident marker
export const INCIDENT_AREA_POPUP_VERTICAL_OFFSET = INCIDENT_AREA_MARKER_TOTAL_VERTICAL_OFFSET + INCIDENT_AREA_MARKER_HEIGHT / 2; // Offset in pixels of the incident area popup from the incident area marker
const INCIDENT_AREA_MARKER_MIN_ZOOM = 9; // Minimum zoom level to show incident area markers

/**
 * TODO: Potentially refactor this function to be more generic and reusable?
 * Incident hazards that have a polygon feature need to be accompanied by a marker
 * that represents the last time the feature was updated.
 *
 * Lacking any other information, the marker will be placed at the center of the polygon.
 *
 * This function is a copy of the updateClusterMarkers function, with the necessary
 * changes to work with incident areas.
 */
export const updateIncidentAreaMarkers = async () => {
  const map = useMapStore();
  const mapbox = map.mapbox!;

  const newMarkers: Record<string, Marker> = {};
  const source = mapbox.getSource(MapboxSource.hazardsFireShapePolygonsSource);

  const zoom = mapbox.getZoom();
  const shouldShowMarkers = zoom > INCIDENT_AREA_MARKER_MIN_ZOOM;

  if (shouldShowMarkers) {
    if (!source || source.type != 'geojson') return;

    const features = mapbox.querySourceFeatures(MapboxSource.hazardsMarkersSource, {
      filter: ['has', 'fireShapeLabel'], // Filter for the relevant incident features
    });

    // For every (incident area) feature on the screen:
    // - find the corresponding incident feature in the hazardsMarkersSource
    // - calculate the coordinates of the incident area marker from the incident feature
    // - create an HTML marker for it (if we didn't yet),
    // - add it to the map if it's not there already
    // !NOTE: The marker is not rendered when the incident area feature is not on the screen. We need to check with product whether this is the desired behaviour.
    for (const feature of features) {
      const incidentId: string = feature.properties!.id;

      if (!feature?.geometry) {
        continue;
      }

      const incidentMarkerCoordinates = (feature.geometry as turfPoint).coordinates as LngLatLike;
      let marker = map.incidentAreaMarkers[incidentId];

      if (!marker) {
        const el = document.createElement('div');

        marker = map.incidentAreaMarkers[incidentId] = new Marker(el, {
          offset: [0, INCIDENT_AREA_MARKER_TOTAL_VERTICAL_OFFSET],
        }).setLngLat(incidentMarkerCoordinates);

        const popupComponent = h(UiMapIncidentAreaMarker, {
          incidentId,
          marker,
          coordinates: incidentMarkerCoordinates,
        });
        render(popupComponent, el);
      }

      newMarkers[incidentId] = marker;
      if (!map.incidentAreaMarkersOnScreen[incidentId]) marker.addTo(mapbox);
    }
  }

  // Remove previously added markers that are no longer visible.
  for (const id in map.incidentAreaMarkersOnScreen) {
    if (!newMarkers[id] || !shouldShowMarkers) {
      map.incidentAreaMarkersOnScreen[id].remove();
      delete map.incidentAreaMarkersOnScreen[id];
    }
  }
  map.incidentAreaMarkersOnScreen = newMarkers;
};
